<template>
	<main id="liste" v-cloak>
		<FicheHeader :invoice="invoice" :swiper_tabs="swiper_tabs" />

		<div id="content">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12">
						<div>
							<LoadingSpinner class="col-12" v-if="!tab_loaded" />
							<!-- La clé porte la facture : d'un contrôle au suivant, seul le paramètre du parent
							     change, et vue-router réutiliserait l'onglet tel quel — avec le formulaire de
							     la facture précédente encore ouvert dans son volet. -->
							<router-view v-if="tab_loaded" :key="$route.params.invoicesupplier_id" :invoice="invoice" @invoice-updated="refreshInvoice" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>


<script type="text/javascript">
	import Navigation from "@/mixins/Navigation.js"
	import Invoice from "@/mixins/Invoice.js"

	export default {
		name: "InvoiceSupplierFiche",

		mixins: [Navigation, Invoice],

		data () {
			return {
				param_tab: '',
				actual_tab: 'invoiceSupplierFicheOverview',
				tab_loaded: false,
				invoice: null
			}
		},

		computed: {
			/**
			 * Onglets de la fiche. Chacun rappelle l'identifiant de la facture : le Swiper construit
			 * ses liens par nom de route, et sans ce paramètre la navigation perdrait la facture.
			 *
			 * Le contrôle n'est proposé que sur une facture déposée à la main : les données d'une
			 * facture reçue d'une plateforme viennent du document électronique, et ne se corrigent pas
			 * au clavier.
			 */
			swiper_tabs() {
				const params = { invoicesupplier_id: this.$route.params.invoicesupplier_id }
				const is_manual = !!(this.invoice && this.invoice.suppliersocr_manual)
				// Les montants sont arrêtés dès que la facture est entrée dans le circuit : par notre
				// contrôle, ou par une prise en charge déclarée au fournisseur — depuis le cycle de vie
				// ou sur la plateforme elle-même. Dans tous ces cas, son règlement peut être saisi.
				const is_checked = !!(this.invoice && (this.invoice.suppliersocr_ocr_status === 'checked' || this.invoice.suppliersocr_acknowledged))

				return [{
					id: 'overview',
					label: 'invoice.supplier_tab_overview',
					active: false,
					href: 'invoiceSupplierFicheOverview',
					params
				}, {
					id: 'control',
					label: 'invoice.supplier_tab_control',
					active: false,
					href: 'invoiceSupplierFicheControl',
					params
				}, {
					id: 'details',
					label: 'invoice.supplier_tab_details',
					active: false,
					href: 'invoiceSupplierFicheDetails',
					params
				}, {
					id: 'ventilation',
					label: 'invoice.supplier_tab_ventilation',
					active: false,
					href: 'invoiceSupplierFicheVentilation',
					params,
					// Ventiler une facture dont les montants n'ont pas encore été vérifiés reviendrait à
					// imputer des sommes qui bougeront encore.
					disabled: !is_checked,
					disabled_reason: 'invoice.supplier_tab_ventilation_disabled'
				}, {
					id: 'ventilation2',
					label: 'invoice.supplier_tab_ventilation2',
					active: false,
					href: 'invoiceSupplierFicheVentilation2',
					params,
					// Nouvelle ventilation, ouverte aux seuls administrateurs le temps de la valider.
					hidden: !this.$store.state.userAccess.hasAdminAccess,
					disabled: !is_checked,
					disabled_reason: 'invoice.supplier_tab_ventilation_disabled'
				}, {
					id: 'lifecycle',
					label: 'invoice.supplier_tab_lifecycle',
					active: false,
					href: 'invoiceSupplierFicheLifecycle',
					params,
					// Une facture déposée à la main n'a pas transité par une plateforme : il n'existe
					// aucun cycle de vie à lui montrer, et aucun statut à émettre.
					disabled: is_manual,
					disabled_reason: 'invoice.supplier_tab_lifecycle_disabled'
				}, {
					id: 'payment',
					label: 'invoice.supplier_tab_payment',
					active: false,
					href: 'invoiceSupplierFichePayment',
					params,
					// Régler une facture dont les montants n'ont pas encore été vérifiés reviendrait à
					// imputer une somme qui n'est pas arrêtée.
					disabled: !is_checked,
					disabled_reason: 'invoice.supplier_tab_payment_disabled'
				}]
			}
		},

		methods: {
			/**
			 * Recharge la facture après une décision prise dans un onglet : le bandeau porte le statut
			 * courant, il doit suivre. Le contenu reste affiché pendant l'appel, la fiche n'étant pas
			 * remise à zéro pour autant.
			 */
			async refreshInvoice() {
				const invoice = await this.getInvoiceSupplierOCR(this.$route.params.invoicesupplier_id)

				// La facture a pu disparaître entre-temps — un doublon arbitré supprime la plus
				// ancienne : rester sur une fiche vide n'aurait pas de sens.
				if(!invoice) {
					this.$router.push({ name: 'invoiceSupplierControl' })
					return
				}

				this.invoice = invoice
			},

			/**
			 * Charge la facture de la fiche. L'identifiant est passé par le routeur quand il annonce
			 * un changement : à cet instant, la route courante porte encore la facture précédente.
			 */
			async init_component(invoicesupplier_id = null) {
				this.tab_loaded = false
				this.invoice = null

				invoicesupplier_id = invoicesupplier_id || this.$route.params.invoicesupplier_id

				if(!invoicesupplier_id) {
					this.$router.push({ name: 'invoiceSupplierControl' })
					return
				}

				const invoice = await this.getInvoiceSupplierOCR(invoicesupplier_id)

				// Une facture inconnue, supprimée ou hors licence renvoie à la liste plutôt que de
				// laisser une fiche vide à l'écran, en disant pourquoi : le lien suivi pouvait être ancien.
				if(!invoice) {
					this.failureToast('invoice.supplier_deleted_or_unknown')
					this.$router.push({ name: 'invoiceSupplierControl' })
					return
				}

				this.invoice = invoice
				this.tab_loaded = true

				const supplier = invoice.tier ? invoice.tier.tiers_rs : ''
				document.title = [supplier, invoice.suppliersocr_num].filter(part => part).join(' - ') || "Groomy"
			}
		},

		mounted() {
			this.param_tab = this.$route.params.actual_tab
			this.init_component()
		},

		/**
		 * Passer d'une facture à l'autre garde la même route : vue-router réutilise alors cette vue
		 * sans la remonter, et elle resterait sur la facture chargée à l'ouverture. C'est ici, et non
		 * dans un watch, que le routeur annonce ce changement.
		 */
		beforeRouteUpdate(to, from, next) {
			next()

			if(to.params.invoicesupplier_id !== from.params.invoicesupplier_id) {
				this.init_component(to.params.invoicesupplier_id)
			}
		},

		components: {
			FicheHeader: () => import('@/components/InvoiceSupplier/Fiche/Header'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
}
</script>
